import type { SceneHost, SceneObject } from "../SceneHost";
import { getStickySettings, stickyStyleFields, STICKY_SIZE } from "../stickySession";
import { applyDocumentContext, documentContextAt } from "../document/documentContext";
import { applyFrameContext, frameAtPoint } from "../frame/frameContext";
import { isBoardEditorChrome, isBoardWidgetChrome } from "./boardWidgetChrome";

export type StickyToolBinding = {
  destroy: () => void;
};

export function bindStickyTool(host: SceneHost): StickyToolBinding {
  function onPointerDown(ev: PointerEvent) {
    if (!host.canEdit || host.getActiveTool() !== "sticky") return;
    if (host.isInteractionBlocked()) return;
    if (isBoardEditorChrome(ev.target)) return;
    if (isBoardWidgetChrome(ev.target)) return;
    if (ev.pointerType === "mouse" && ev.button !== 0) return;
    ev.preventDefault();

    const pt = host.stagePoint(ev.clientX, ev.clientY);
    const settings = getStickySettings();
    const draft: SceneObject = {
      id: crypto.randomUUID(),
      type: "sticky",
      x: Math.round(pt.x - STICKY_SIZE.w / 2),
      y: Math.round(pt.y - STICKY_SIZE.h / 2),
      w: STICKY_SIZE.w,
      h: STICKY_SIZE.h,
      text: "",
      style: stickyStyleFields(settings),
    };

    applyDocumentContext(draft, documentContextAt(host.getObjects(), pt.x, pt.y));
    applyFrameContext(draft, frameAtPoint(host.getObjects(), pt.x, pt.y));

    void host.addObject(draft, `sticky-${draft.id}`).then(() => {
      host.setSelection([draft.id]);
      host.setActiveTool?.("select");
      host.requestTextEdit?.(draft.id);
    });
  }

  host.hitSurface.addEventListener("pointerdown", onPointerDown);

  return {
    destroy() {
      host.hitSurface.removeEventListener("pointerdown", onPointerDown);
    },
  };
}
